import { useMemo } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { ProfileFilter } from '../components/ProfileFilter';
import { DashPanel } from '../components/DashPanel';
import { MetricStat } from '../components/MetricStat';
import { RankedList } from '../components/RankedList';
import { exportCardCsv } from '../utils/csvExport';
import { computeRealMusicAnalytics } from '../utils/realMusicAnalytics';

export function MusicModule() {
  const { filteredProfiles, profilesLoading } = useDashboard();

  const analytics = useMemo(() => computeRealMusicAnalytics(filteredProfiles), [filteredProfiles]);
  const topGenreShare = analytics.genreShare[0];

  return (
    <div>
      <ProfileFilter />

      {!profilesLoading && filteredProfiles.length === 0 && (
        <p className="text-sm text-muted-foreground mb-6">
          Nenhum perfil encontrado para esse filtro no Firestore.
        </p>
      )}

      <div className="mb-6 pb-4 border-b border-border flex flex-wrap gap-8">
        <div>
          <p className="text-xs text-muted-foreground">Reproduções totais</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">{analytics.totalPlays.toLocaleString('pt-BR')}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Música mais tocada</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">{analytics.mostPlayedSong?.name ?? '—'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Gênero líder</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">{analytics.topGenres[0]?.name ?? '—'}</p>
          <p className="text-xs text-muted-foreground">{topGenreShare?.pct ?? 0}% das reproduções</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Músicas reproduzidas</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">{analytics.songsUsed}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Gêneros</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">{analytics.genresUsed}</p>
        </div>
      </div>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Diversidade e concentração de uso</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <DashPanel padding="sm">
            <MetricStat label="Gêneros reproduzidos" value={analytics.genresUsed} />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat
              label="Top 5 músicas"
              value={analytics.concentrationTop5.pct}
              suffix="% das reproduções"
            />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat
              label="Top 10 músicas"
              value={analytics.concentrationTop10.pct}
              suffix="% das reproduções"
            />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat
              label="⭐ Favoritas implícitas"
              value={analytics.topFavorites.length}
              suffix="top 5 mais tocadas"
            />
          </DashPanel>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          As top 5 músicas concentram{' '}
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
            {analytics.concentrationTop5.pct}%
          </span>{' '}
          das reproduções — quanto maior esse número, mais os usuários se concentram em poucas
          músicas em vez de explorar o catálogo.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Músicas mais reproduzidas</h3>
        <RankedList
          title="Ranking por reprodução"
          rows={analytics.topSongs.map((r) => ({ label: r.name, value: r.value.toLocaleString('pt-BR') }))}
          onExport={() =>
            exportCardCsv(
              'musica-mais-reproduzidas',
              ['Música', 'Reproduções'],
              analytics.topSongs.map((r) => [r.name, r.value])
            )
          }
        />
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Gêneros mais reproduzidos</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankedList
            title="Ranking por reprodução"
            rows={analytics.topGenres.map((r) => ({ label: r.name, value: r.value.toLocaleString('pt-BR') }))}
            onExport={() =>
              exportCardCsv(
                'musica-generos-mais-reproduzidos',
                ['Gênero', 'Reproduções'],
                analytics.topGenres.map((r) => [r.name, r.value])
              )
            }
          />
          <RankedList
            title="Participação de cada gênero"
            rows={analytics.genreShare.map((r) => ({
              label: r.genre,
              value: `${r.value.toLocaleString('pt-BR')} (${r.pct}%)`,
              pct: r.pct,
            }))}
            onExport={() =>
              exportCardCsv(
                'musica-participacao-generos',
                ['Gênero', 'Reproduções', 'Participação (%)'],
                analytics.genreShare.map((r) => [r.genre, r.value, r.pct])
              )
            }
          />
        </div>
      </section>
    </div>
  );
}
